import { useState, useCallback } from 'react';
import { CSV_FILE_CONFIGS } from '~/app/constants/csvAllowedFields';
import { useLookupQueries } from './useLookupQueries';
import { useCsvFileUpload } from './useCsvFileUpload';
import { useCrudOperations } from './useCrudOperations';
import { useAssociations } from './useAssociations';

// ── Orchestrator Hook ───────────────────────────────────────────────

export const useCsvUpload = () => {
  const [activeTab, setActiveTab] = useState<string>(CSV_FILE_CONFIGS[0].name);

  // 1. Lookup queries (pagination, search, react-query cache)
  const {
    queryClient,
    lookups,
    paginationState,
    searchTerms,
    subsystemData,
    assetTypeData,
    componentTypeData,
    setLocalLookupOverrides,
    setPagination,
    onPageChange,
    onSearch,
    onRefresh,
  } = useLookupQueries();

  // 2. CSV file upload / parse / submit
  const {
    uploads,
    viewModes,
    submitting,
    onToggleView,
    onFileUpload,
    onFileRemove,
    onDownloadTemplate,
    onSubmit,
    onMatchySubmit,
  } = useCsvFileUpload(queryClient, paginationState, setPagination);

  // 3. CRUD row operations
  const {
    editingRows,
    onAddRow,
    onEditRow,
    onDeleteRow,
    onSaveRow,
    onCancelEdit,
    onApplySynonyms,
  } = useCrudOperations(queryClient, lookups, onRefresh);

  // 4. Associations & transfer modal
  const {
    associations,
    transferModal,
    onOpenTransfer,
    onCloseTransfer,
    onSaveTransfer,
  } = useAssociations(
    subsystemData,
    assetTypeData,
    componentTypeData,
    lookups,
    setLocalLookupOverrides,
    onRefresh,
  );

  const handleTabChange = useCallback(
    (tab: string) => {
      setActiveTab(tab);
      onRefresh(tab);
    },
    [onRefresh],
  );

  return {
    fileConfigs: CSV_FILE_CONFIGS,
    activeTab,
    viewModes,
    uploads,
    lookups,
    submitting,
    editingRows,
    associations,
    transferModal,
    onTabChange: handleTabChange,
    onToggleView,
    onFileUpload,
    onFileRemove,
    onSubmit,
    onDownloadTemplate,
    onAddRow,
    onEditRow,
    onDeleteRow,
    onSaveRow,
    onCancelEdit,
    onApplySynonyms,
    onRefresh,
    onMatchySubmit,
    onOpenTransfer,
    onCloseTransfer,
    onSaveTransfer,
    pagination: paginationState,
    onPageChange,
    searchTerms,
    onSearch,
  };
};
